import { ProductCategory } from "gen/company/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { useMemo } from "react"
import { UUID } from "shared-ts/classes/uuid"
import { stringComparator } from "shared-ts/utils/sorting/comparators"

import { useListProductCategoriesForCompanyV2Query } from "features/api/company"

import useLocalSearchPopoverMultiSelect, {
  UseLocalSearchPopoverMultiSelectProps,
  UseLocalSearchPopoverSelectRes,
} from "./useLocalSearchPopoverMultiSelect"

interface UseCategoriesPopoverMultiSelectProps
  extends Omit<
    UseLocalSearchPopoverMultiSelectProps<ProductCategory>,
    "items" | "keyExtractor" | "labelExtractor" | "contentExtractor"
  > {
  type: "department" | "class" | "fineline"
}

export interface UseCategoriesPopoverMultiSelectRes
  extends UseLocalSearchPopoverSelectRes<ProductCategory> {
  selectedIds: PBUUID[]
}

export function useCategoriesPopoverMultiSelect({
  type,
  ...rest
}: UseCategoriesPopoverMultiSelectProps): UseCategoriesPopoverMultiSelectRes {
  const { data } = useListProductCategoriesForCompanyV2Query({})

  const sortedCategories = useMemo(() => {
    let categories: ProductCategory[] = []
    switch (type) {
      case "department":
        categories = data?.departments ?? []
        break
      case "class":
        categories = data?.classes ?? []
        break
      case "fineline":
        categories = data?.finelines ?? []
        break
    }
    return [...categories].sort(stringComparator((category) => category.name))
  }, [data, type])

  const res = useLocalSearchPopoverMultiSelect({
    items: sortedCategories,
    keyExtractor,
    labelExtractor,
    ...rest,
  })

  const selectedIds = useMemo(() => {
    if (!res.selected) return []
    return res.selected
      .map((category) => category.id)
      .filter((id): id is PBUUID => id !== undefined)
  }, [res.selected])

  return {
    ...res,
    selectedIds,
  }
}

function keyExtractor(category: ProductCategory): string {
  return UUID.idString(category.id)
}

function labelExtractor(category: ProductCategory): string {
  return category.name
}
